import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import {
  ChevronLeft, Loader2, Pencil, Copy, Star, Archive, Trash2, Plus,
  Clock, Users, Award, CheckCircle2, Calendar, Eye, FileText,
  GraduationCap, Shield, ShoppingBag,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";

const DETAIL_TABS = [
  { k: "mon", l: "Mon programme" },
  { k: "evaluations", l: "Evaluations" },
  { k: "apprenant", l: "Espace Apprenant" },
  { k: "documents", l: "Documents prives" },
  { k: "inscriptions", l: "Inscriptions" },
  { k: "qualite", l: "Qualite" },
  { k: "marketplace", l: "Marketplace" },
];

export default function ProgramDetailPage() {
  const { program_id } = useParams();
  const navigate = useNavigate();
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [prog, setProg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("mon");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/lms/programs/${program_id}`);
      setProg(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  // eslint-disable-next-line
  }, [program_id]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [program_id]);

  const duplicate = async () => {
    try {
      const r = await api.post(`/lms/programs/${program_id}/duplicate`);
      toast.success("Programme duplique");
      navigate(`/admin/formation/bibliotheque/${r.data.program_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const archive = async () => {
    if (!window.confirm("Archiver ce programme ?")) return;
    try {
      await api.post(`/lms/programs/${program_id}/archive`);
      toast.success("Archive");
      navigate("/admin/formation/bibliotheque");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const del = async () => {
    if (!window.confirm("Supprimer definitivement ? Cascade sequences, sessions, enrollments.")) return;
    try {
      await api.delete(`/lms/programs/${program_id}`);
      toast.success("Supprime");
      navigate("/admin/formation/bibliotheque");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  if (loading) return <div className="p-8"><Loader2 className="w-8 h-8 animate-spin text-[#f7c935] mx-auto mt-20" /></div>;
  if (!prog) return <div className="p-8 text-slate-400">Programme non trouve</div>;

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="program-detail-page">
      <PageHeader
        icon={(<span className="w-3 h-3 rounded-full block" style={{ background: prog.color || "#f59e0b" }} />)}
        title={prog.title}
        description={prog.sous_titre}
        breadcrumb={[{ label: "Formation" }, { label: "Programmes" }, { label: prog.title }]}
        testId="program-detail-header"
        actions={(
          <Button onClick={() => navigate("/admin/formation/bibliotheque")} variant="ghost" className="text-slate-400 hover:bg-white/5" data-testid="back-biblio-btn">
            <ChevronLeft className="w-4 h-4 mr-1" /> Bibliothèque
          </Button>
        )}
      />

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-white/10 overflow-x-auto">
        {DETAIL_TABS.map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            data-testid={`detail-tab-${t.k}`}
            className={`px-4 py-3 text-sm font-semibold transition-colors whitespace-nowrap ${tab === t.k ? "text-[#f7c935] border-b-2 border-[#f7c935]" : "text-slate-400 hover:text-white"}`}>
            {t.l}
          </button>
        ))}
      </div>

      {/* Action bar */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Button onClick={() => navigate(`/admin/formation/bibliotheque/${program_id}/edit`)} className="bg-[#f7c935] text-[#14101c] font-bold hover:bg-[#ffd948]" data-testid="edit-content-btn">
          <Pencil className="w-4 h-4 mr-2" /> Modifier le contenu
        </Button>
        <Button variant="outline" onClick={() => navigate(`/admin/formations?newSessionFor=${program_id}`)} className="border-white/10 text-white hover:bg-white/5" data-testid="create-session-btn">
          <Calendar className="w-4 h-4 mr-2" /> Creer une session
        </Button>
        <Button variant="outline" onClick={duplicate} className="border-white/10 text-white hover:bg-white/5" data-testid="duplicate-btn">
          <Copy className="w-4 h-4 mr-2" /> Dupliquer
        </Button>
        <Button variant="outline" onClick={archive} className="border-white/10 text-white hover:bg-white/5" data-testid="archive-btn">
          <Archive className="w-4 h-4 mr-2" /> Archiver
        </Button>
        <Button variant="outline" onClick={del} className="border-red-500/40 text-red-400 hover:bg-red-500/10" data-testid="delete-btn">
          <Trash2 className="w-4 h-4 mr-2" /> Supprimer
        </Button>
      </div>

      {/* Tab content */}
      {tab === "mon" && <MonProgrammeTab prog={prog} />}
      {tab === "evaluations" && <EvaluationsTab program_id={program_id} api={api} />}
      {tab === "apprenant" && <StubTab icon={GraduationCap} title="Espace Apprenant" description="Acces stagiaires, sequences e-learning, progression. Gere dans l'onglet LMS principal." />}
      {tab === "documents" && <StubTab icon={FileText} title="Documents prives" description="Supports de cours, ressources formateur, documents administratifs internes." />}
      {tab === "inscriptions" && <InscriptionsTab program_id={program_id} api={api} />}
      {tab === "qualite" && <QualiteTab program_id={program_id} api={api} />}
      {tab === "marketplace" && <StubTab icon={ShoppingBag} title="Marketplace" description="Publier le programme sur le marketplace public Blitzz (a venir)." />}
    </div>
  );
}

// ─── Tab: Mon programme (aperçu Digiforma-style) ───────────
function MonProgrammeTab({ prog }) {
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <div className="bg-[#14101c] border border-white/10 rounded-2xl p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold">{prog.title}</h2>
              {prog.sous_titre && <div className="text-[#f7c935] mt-1 text-sm">{prog.sous_titre}</div>}
              <div className="flex gap-2 mt-2">
                <span className="text-xs px-2 py-0.5 rounded bg-white/5">{prog.modality}</span>
                {prog.eligible_cpf && <span className="text-xs px-2 py-0.5 rounded bg-blue-500/20 text-blue-300">Eligible CPF</span>}
                {prog.publie_catalogue && <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-300">Publie catalogue</span>}
                <span className="text-xs px-2 py-0.5 rounded bg-white/5 font-mono">{prog.code_interne}</span>
              </div>
            </div>
          </div>

          {prog.description && (
            <div className="prose prose-invert max-w-none text-sm mb-6 whitespace-pre-wrap">{prog.description}</div>
          )}

          {prog.section_description && (
            <section className="mb-6">
              <h3 className="font-bold text-slate-300 uppercase text-xs tracking-wider mb-2">Description</h3>
              <div className="text-sm text-slate-300 whitespace-pre-wrap">{prog.section_description}</div>
            </section>
          )}

          {Array.isArray(prog.section_objectifs_pedagogiques) && prog.section_objectifs_pedagogiques.length > 0 && (
            <section className="mb-6">
              <h3 className="font-bold text-slate-300 uppercase text-xs tracking-wider mb-2">Objectifs pedagogiques</h3>
              <div className="space-y-2 border-l-2 border-[#f7c935]/30 pl-4">
                {prog.section_objectifs_pedagogiques.map((o, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-[#f7c935] flex-shrink-0 mt-0.5" /> {o}
                  </div>
                ))}
              </div>
            </section>
          )}

          {Array.isArray(prog.objectives) && prog.objectives.length > 0 && prog.section_objectifs_pedagogiques?.length === 0 && (
            <section className="mb-6">
              <h3 className="font-bold text-slate-300 uppercase text-xs tracking-wider mb-2">Objectifs</h3>
              <ul className="space-y-1 text-sm text-slate-300 list-disc pl-5">{prog.objectives.map((o, i) => <li key={i}>{o}</li>)}</ul>
            </section>
          )}

          {prog.section_profil_apprenants && <InfoSection title="Profil des apprenants" content={prog.section_profil_apprenants} />}
          {prog.section_contenu_pedagogique && <InfoSection title="Contenu de la formation" content={prog.section_contenu_pedagogique} />}
          {prog.section_modalites && <InfoSection title="Modalites" content={prog.section_modalites} />}
          {prog.section_type_action && <InfoSection title="Type d'action de formation" content={prog.section_type_action} />}
          {prog.section_suivi_execution && <InfoSection title="Suivi de l'execution" content={prog.section_suivi_execution} />}
          {prog.section_modalites_certification && <InfoSection title="Modalites de certification" content={prog.section_modalites_certification} />}
          {prog.section_qualite && <InfoSection title="Qualite" content={prog.section_qualite} />}
          {Array.isArray(prog.section_domaines_competences) && prog.section_domaines_competences.length > 0 && (
            <section className="mb-6">
              <h3 className="font-bold text-slate-300 uppercase text-xs tracking-wider mb-2">Domaines de competences</h3>
              <div className="flex flex-wrap gap-1">{prog.section_domaines_competences.map((d, i) => <span key={i} className="text-xs bg-white/5 px-2 py-1 rounded">{d}</span>)}</div>
            </section>
          )}

          {prog.certification && (
            <div className="mt-6 p-3 bg-[#f7c935]/10 border border-[#f7c935]/20 rounded flex items-center gap-2">
              <Award className="w-4 h-4 text-[#f7c935]" />
              <span className="text-sm">{prog.certification}</span>
            </div>
          )}
        </div>
      </div>

      {/* Sidebar */}
      <aside className="space-y-4">
        <div className="bg-[#14101c] border border-white/10 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3 text-slate-400 text-xs uppercase font-bold tracking-wider"><Clock className="w-4 h-4" />Duree</div>
          <div className="text-2xl font-black">{prog.duration_hours || 0} heures</div>
          <div className="text-sm text-slate-400">({prog.duration_days || 0} jours)</div>
        </div>

        <div className="bg-[#14101c] border border-white/10 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3 text-slate-400 text-xs uppercase font-bold tracking-wider">Tarifs HT</div>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-slate-400">Entreprise</span><span className="font-bold">{prog.prix_entreprise || prog.price_ht || 0} €</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Particulier</span><span className="font-bold">{prog.prix_particulier || prog.price_ht || 0} €</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Independant</span><span className="font-bold">{prog.prix_independant || prog.price_ht || 0} €</span></div>
          </div>
        </div>

        {(prog.version || prog.version_date) && (
          <div className="bg-[#14101c] border border-white/10 rounded-2xl p-5">
            <div className="text-slate-400 text-xs uppercase font-bold tracking-wider mb-2">Version</div>
            <div className="text-sm"><span className="font-bold">{prog.version || "—"}</span>{prog.version_date && <span className="text-slate-500 ml-2">{prog.version_date}</span>}</div>
          </div>
        )}

        {prog.variants_count > 0 && (
          <div className="bg-[#14101c] border border-white/10 rounded-2xl p-5">
            <div className="flex items-center gap-2 text-slate-400 text-xs uppercase font-bold tracking-wider mb-2"><Star className="w-3 h-3" />Variantes</div>
            <div className="text-3xl font-black">{prog.variants_count}</div>
            <div className="text-xs text-slate-500 mt-1">Chaque session snapshote le programme</div>
          </div>
        )}

        <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-2xl p-4 text-xs text-slate-300">
          <div className="font-bold text-yellow-400 mb-1">Info</div>
          Toute modification de ce programme depuis une session cree une variante qui apparait ici et n'affecte pas le programme maitre.
        </div>
      </aside>
    </div>
  );
}

function InfoSection({ title, content }) {
  return (
    <section className="mb-6">
      <h3 className="font-bold text-slate-300 uppercase text-xs tracking-wider mb-2">{title}</h3>
      <div className="text-sm text-slate-300 whitespace-pre-wrap">{content}</div>
    </section>
  );
}

function StubTab({ icon: Icon, title, description }) {
  return (
    <div className="bg-[#14101c] border border-white/10 rounded-2xl p-10 text-center">
      <Icon className="w-12 h-12 mx-auto text-slate-600 mb-3" />
      <h3 className="font-bold text-white mb-1">{title}</h3>
      <p className="text-sm text-slate-400 max-w-md mx-auto">{description}</p>
      <div className="text-xs text-slate-500 mt-3">Prochainement disponible.</div>
    </div>
  );
}

// ─── Inscriptions Tab ──────────────────────────────────────
function InscriptionsTab({ program_id, api }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/lms/programs/${program_id}/enrollments`)
      .then(r => { setRows(r.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  // eslint-disable-next-line
  }, [program_id]);

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-[#f7c935] mx-auto my-10" />;
  if (rows.length === 0) return <div className="text-center py-16 text-slate-500 border border-dashed border-white/10 rounded-xl">Aucune inscription pour le moment</div>;

  return (
    <div className="space-y-3">
      <div className="bg-[#14101c] border border-white/10 rounded-xl p-4 grid grid-cols-4 gap-2 text-center text-sm">
        <KPI icon={Users} label="Total" value={rows.length} />
        <KPI icon={CheckCircle2} label="Termines" value={rows.filter(r => r.status === "termine").length} />
        <KPI icon={Calendar} label="En cours" value={rows.filter(r => r.status === "en_cours").length} />
        <KPI icon={Eye} label="Sessions" value={new Set(rows.map(r => r.session_id)).size} />
      </div>
      <div className="overflow-x-auto bg-[#14101c] border border-white/10 rounded-xl">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-xs uppercase text-slate-400">
            <tr>
              <th className="px-4 py-2 text-left">Stagiaire</th>
              <th className="px-4 py-2 text-left">Session</th>
              <th className="px-4 py-2 text-left">Date</th>
              <th className="px-4 py-2 text-left">Statut</th>
              <th className="px-4 py-2 text-left">Progression</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {rows.map(e => (
              <tr key={e.enrollment_id} data-testid={`insc-${e.enrollment_id}`}>
                <td className="px-4 py-2 font-semibold">{e.student_name}<br /><span className="text-xs text-slate-500">{e.student_email}</span></td>
                <td className="px-4 py-2">{e.session?.title}</td>
                <td className="px-4 py-2 text-xs text-slate-400">{e.session?.start_date} → {e.session?.end_date}</td>
                <td className="px-4 py-2"><span className="text-xs bg-white/5 px-2 py-0.5 rounded">{e.status}</span></td>
                <td className="px-4 py-2">
                  <div className="w-20 bg-white/5 rounded h-1.5"><div className="bg-[#f7c935] h-1.5 rounded" style={{ width: `${e.progress || 0}%` }} /></div>
                  <span className="text-[10px] text-slate-500">{e.progress || 0}%</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value }) {
  return (
    <div className="p-3">
      <Icon className="w-4 h-4 mx-auto text-[#f7c935] mb-1" />
      <div className="text-2xl font-black">{value}</div>
      <div className="text-xs text-slate-400">{label}</div>
    </div>
  );
}

// ─── Qualité Tab ───────────────────────────────────────────
function QualiteTab({ program_id, api }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/lms/programs/${program_id}/qualite`)
      .then(r => { setStats(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  // eslint-disable-next-line
  }, [program_id]);

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-[#f7c935] mx-auto my-10" />;
  if (!stats) return null;

  const cards = [
    { l: "Sessions totales", v: stats.total_sessions, icon: Calendar, color: "#3b82f6" },
    { l: "Sessions terminees", v: stats.completed_sessions, icon: CheckCircle2, color: "#84cc16" },
    { l: "Stagiaires inscrits", v: stats.total_enrolled, icon: Users, color: "#f59e0b" },
    { l: "Stagiaires termines", v: stats.completed, icon: GraduationCap, color: "#10b981" },
    { l: "Taux de reussite", v: `${stats.taux_reussite_pct}%`, icon: Award, color: "#f7c935" },
    { l: "Taux satisfaction", v: `${stats.taux_satisfaction_pct}%`, icon: Star, color: "#ec4899" },
  ];

  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-3 gap-3">
        {cards.map(c => (
          <div key={c.l} className="bg-[#14101c] border border-white/10 rounded-2xl p-5">
            <div className="flex items-center gap-2 text-xs text-slate-400 uppercase font-bold mb-2"><c.icon className="w-4 h-4" style={{ color: c.color }} />{c.l}</div>
            <div className="text-3xl font-black">{c.v}</div>
          </div>
        ))}
      </div>
      <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-xl p-4 text-xs text-slate-300 flex items-start gap-3">
        <Shield className="w-5 h-5 text-yellow-400 mt-0.5" />
        <div>
          <div className="font-bold text-yellow-400 mb-1">Indicateurs Qualiopi</div>
          Les indicateurs remontes correspondent aux indicateurs Qualiopi C8 (satisfaction) et C9 (reussite). Le taux de satisfaction sera calcule automatiquement a partir des evaluations a chaud (onglet Evaluations).
        </div>
      </div>
    </div>
  );
}


// ─── Evaluations Tab ───────────────────────────────────────
function EvaluationsTab({ program_id, api }) {
  const [agg, setAgg] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    Promise.all([
      api.get(`/evaluations/aggregate?program_id=${program_id}`),
      api.get(`/evaluations/responses?program_id=${program_id}`),
    ]).then(([a, r]) => { setAgg(a.data); setResponses(r.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  // eslint-disable-next-line
  }, [program_id]);
  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-[#f7c935] mx-auto my-10" />;
  if (!agg) return null;
  const blocks = [
    { k: "pre", l: "Pre-formation" },
    { k: "hot", l: "A chaud" },
    { k: "cold", l: "A froid" },
    { k: "manager", l: "Managers" },
  ];
  return (
    <div className="space-y-4" data-testid="evaluations-tab">
      <div className="grid md:grid-cols-4 gap-3">
        {blocks.map(b => {
          const x = agg[b.k] || {};
          return (
            <div key={b.k} className="bg-[#14101c] border border-white/10 rounded-2xl p-4">
              <div className="text-xs text-slate-400 uppercase font-bold mb-1">{b.l}</div>
              <div className="text-2xl font-black">{x.count || 0}</div>
              <div className="text-[10px] text-slate-500">reponses</div>
              {x.avg_rating !== null && x.avg_rating !== undefined && (
                <div className="mt-2 flex gap-2 text-xs">
                  <span className="text-[#f7c935]">★ {x.avg_rating}/5</span>
                  {x.satisfaction_pct !== null && <span className="text-slate-400">{x.satisfaction_pct}% satisf.</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="bg-[#14101c] border border-white/10 rounded-2xl p-5 flex items-center gap-4">
        <Award className="w-8 h-8 text-[#f7c935]" />
        <div>
          <div className="text-xs text-slate-400">Satisfaction globale (à chaud + à froid)</div>
          <div className="text-3xl font-black">{agg.overall_satisfaction_pct || 0}%</div>
          <div className="text-xs text-slate-500 mt-1">NPS: {agg.overall_nps || 0}</div>
        </div>
      </div>
      {responses.length > 0 ? (
        <div className="overflow-x-auto bg-[#14101c] border border-white/10 rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-xs uppercase text-slate-400">
              <tr>
                <th className="px-4 py-2 text-left">Date</th>
                <th className="px-4 py-2 text-left">Type</th>
                <th className="px-4 py-2 text-left">Repondant</th>
                <th className="px-4 py-2 text-left">Note</th>
                <th className="px-4 py-2 text-left">NPS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {responses.map(r => (
                <tr key={r.response_id}>
                  <td className="px-4 py-2 text-xs text-slate-400">{r.submitted_at?.slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-2"><span className="text-xs bg-white/5 px-2 py-0.5 rounded">{r.template_type}</span></td>
                  <td className="px-4 py-2 text-xs">{r.respondent_email}</td>
                  <td className="px-4 py-2">{r.avg_rating !== null ? `★ ${r.avg_rating}/5` : "—"}</td>
                  <td className="px-4 py-2">{r.nps !== null && r.nps !== undefined ? r.nps : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <div className="text-center py-10 text-slate-500 text-sm">Aucune reponse pour le moment. Envoyez une evaluation depuis la Bibliotheque d'evaluations.</div>}
    </div>
  );
}
